// Compound interest calculation utilities
// All monetary values are in EUR

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface CompoundInterestInput {
  initialCapital: number; // EUR
  monthlyContribution: number; // EUR
  annualRate: number; // percentage (e.g., 7 for 7%)
  years: number;
  compoundingFrequency: number; // 1 = annual, 12 = monthly
}

export interface YearlyData {
  year: number;
  contributions: number; // cumulative contributions at end of year
  interest: number; // cumulative interest earned at end of year
  total: number; // total value at end of year
}

export interface CompoundInterestResult {
  finalCapital: number;
  totalContributions: number;
  totalInterest: number;
  yearlyBreakdown: YearlyData[];
}

// ---------------------------------------------------------------------------
// Main calculation
// ---------------------------------------------------------------------------

/**
 * Calculate compound interest with optional regular monthly contributions.
 *
 * Formulas used:
 *   - Initial capital growth:  Vi * (1 + r/n)^(n*t)
 *   - Future value of a series of monthly contributions (annuity):
 *       When the compounding period length differs from the monthly
 *       contribution period we convert the monthly contribution to an
 *       equivalent contribution per compounding period and then apply the
 *       standard future-value-of-annuity formula:
 *         PMT_period * [((1 + r/n)^(n*t) - 1) / (r/n)]
 *       where PMT_period = monthlyContribution * (12 / n)
 *
 *   When the annual rate is 0 the formulas degenerate to simple sums.
 */
export function calculateCompoundInterest(
  input: CompoundInterestInput,
): CompoundInterestResult {
  const { initialCapital, monthlyContribution, annualRate, years, compoundingFrequency } = input;

  const n = compoundingFrequency;
  const r = annualRate / 100; // decimal rate
  const ratePerPeriod = n > 0 ? r / n : 0;

  // Contribution per compounding period
  // e.g. if compounding is monthly (n=12), PMT = monthlyContribution
  //      if compounding is annual  (n=1),  PMT = monthlyContribution * 12
  const contributionPerPeriod = n > 0 ? monthlyContribution * (12 / n) : monthlyContribution * 12;

  const yearlyBreakdown: YearlyData[] = [];

  for (let year = 1; year <= years; year++) {
    const totalPeriods = n * year;

    let total: number;
    let capitalGrowth: number;
    let annuityGrowth: number;

    if (ratePerPeriod === 0) {
      // No interest — simple accumulation
      capitalGrowth = initialCapital;
      annuityGrowth = contributionPerPeriod * totalPeriods;
      total = capitalGrowth + annuityGrowth;
    } else {
      // Future value of the initial lump sum
      const growthFactor = Math.pow(1 + ratePerPeriod, totalPeriods);
      capitalGrowth = initialCapital * growthFactor;

      // Future value of the annuity (series of equal contributions)
      annuityGrowth = contributionPerPeriod * ((growthFactor - 1) / ratePerPeriod);

      total = capitalGrowth + annuityGrowth;
    }

    const cumulativeContributions = initialCapital + monthlyContribution * 12 * year;
    const interest = total - cumulativeContributions;

    yearlyBreakdown.push({
      year,
      contributions: round(cumulativeContributions),
      interest: round(interest),
      total: round(total),
    });
  }

  const lastYear = yearlyBreakdown[yearlyBreakdown.length - 1];

  // Handle the edge case where years === 0
  if (!lastYear) {
    return {
      finalCapital: round(initialCapital),
      totalContributions: round(initialCapital),
      totalInterest: 0,
      yearlyBreakdown: [],
    };
  }

  return {
    finalCapital: lastYear.total,
    totalContributions: lastYear.contributions,
    totalInterest: lastYear.interest,
    yearlyBreakdown,
  };
}

// ---------------------------------------------------------------------------
// Preset scenarios
// ---------------------------------------------------------------------------

export const PRESET_SCENARIOS = {
  youngPro: {
    initialCapital: 5000,
    monthlyContribution: 200,
    annualRate: 7,
    years: 30,
    compoundingFrequency: 12,
  },
  earlySaver: {
    initialCapital: 1000,
    monthlyContribution: 50,
    annualRate: 7,
    years: 40,
    compoundingFrequency: 12,
  },
  parent: {
    initialCapital: 2000,
    monthlyContribution: 100,
    annualRate: 6,
    years: 18,
    compoundingFrequency: 12,
  },
} as const;

export const DEFAULT_INPUT: CompoundInterestInput = {
  initialCapital: 10000,
  monthlyContribution: 100,
  annualRate: 7,
  years: 20,
  compoundingFrequency: 12,
};

// ---------------------------------------------------------------------------
// Format helper
// ---------------------------------------------------------------------------

/**
 * Format a number as Euro with French locale.
 * Example output: "66 212 €"
 */
export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Round to 2 decimal places to avoid floating-point drift. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
